import { Injectable } from '@angular/core';

interface ProjectFilter {
  projectId: string;
  filters: Array<{
    id: string;
    name?: string;
    filter: {
      responsibleId?: string;
      authorId?: string;
      tagId?: string;
      statusId?: string;
      text?: string;
    };
  }>;
}

type ProjectFiltersMap = {
  [projectId: string]: {
    filters: ProjectFilter['filters'];
  };
};

@Injectable({
  providedIn: 'root',
})
export class ProjectFilterService {
  private readonly STORAGE_KEY = 'project_filters';

  /**
   * Проверить, является ли фильтр пустым
   */
  private isEmptyFilter(filter: ProjectFilter['filters'][0]['filter']): boolean {
    return !filter || Object.values(filter).every(value => !value);
  }

  /**
   * Сохранить фильтр для проекта
   */
  saveProjectFilter(projectId: string, filter: ProjectFilter['filters'][0]['filter']): void {
    // Не сохраняем пустые фильтры
    if (this.isEmptyFilter(filter)) {
      return;
    }

    const allFilters = this.getAllProjectFilters();

    // Инициализируем структуру для проекта, если она не существует
    if (!allFilters[projectId]) {
      allFilters[projectId] = {
        filters: [],
      };
    }

    // Генерируем уникальный ID для фильтра
    const filterId = new Date().getTime().toString();

    // Добавляем новый фильтр в массив
    allFilters[projectId].filters.push({
      id: filterId,
      filter: filter,
    });

    console.log('saving filters for project', projectId, allFilters);
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allFilters));
  }

  /**
   * Получить все фильтры проекта
   */
  getProjectFilters(projectId: string): ProjectFilter['filters'] {
    const allFilters = this.getAllProjectFilters();
    return allFilters[projectId]?.filters || [];
  }

  /**
   * Получить последний фильтр проекта
   */
  getLastProjectFilter(projectId: string): ProjectFilter['filters'][0]['filter'] | null {
    const projectFilters = this.getProjectFilters(projectId);
    if (projectFilters.length === 0) {
      return null;
    }

    const lastFilter = projectFilters[projectFilters.length - 1].filter;

    // Не возвращаем пустые фильтры
    if (this.isEmptyFilter(lastFilter)) {
      return null;
    }

    return lastFilter;
  }

  /**
   * Получить все сохраненные фильтры всех проектов
   */
  private getAllProjectFilters(): ProjectFiltersMap {
    const filtersJson = localStorage.getItem(this.STORAGE_KEY);
    return filtersJson ? JSON.parse(filtersJson) : {};
  }

  /**
   * Удалить все фильтры проекта
   */
  clearProjectFilter(projectId: string): void {
    const allFilters = this.getAllProjectFilters();
    delete allFilters[projectId];
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allFilters));
  }

  /**
   * Удалить конкретный фильтр проекта
   */
  deleteProjectFilter(projectId: string, filterId: string): void {
    const allFilters = this.getAllProjectFilters();
    if (allFilters[projectId]) {
      allFilters[projectId].filters = allFilters[projectId].filters.filter(f => f.id !== filterId);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(allFilters));
    }
  }
}
